// Phase 5Z.9 — First-login mandatory password change
import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fahrerChangePassword } from "./lib/api";
import { useFahrerAuth } from "./lib/auth";

export default function FahrerChangePassword() {
  const { me, refresh } = useFahrerAuth();
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (!me) return <Navigate to="/fahrer/login" replace />;

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError(t("auth.password_too_short")); return; }
    if (next !== confirm) { setError(t("auth.passwords_dont_match")); return; }
    setLoading(true);
    try {
      await fahrerChangePassword(current, next);
      await refresh();
      nav("/fahrer", { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || t("auth.login_failed"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col" data-testid="fahrer-change-password-page">
      <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full px-6 py-12">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-zinc-50">{t("auth.first_login_title")}</h1>
          <p className="text-sm text-zinc-400 mt-2">{t("auth.first_login_message")}</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="fahrer-cpw-form">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("auth.current_password")}</label>
            <input
              type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
              data-testid="fahrer-cpw-current"
              className="w-full mt-1.5 px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-base text-zinc-100 focus:border-amber-600 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("auth.new_password")}</label>
            <input
              type="password" value={next} onChange={(e) => setNext(e.target.value)}
              data-testid="fahrer-cpw-new"
              className="w-full mt-1.5 px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-base text-zinc-100 focus:border-amber-600 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("auth.confirm_password")}</label>
            <input
              type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="fahrer-cpw-confirm"
              className="w-full mt-1.5 px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-base text-zinc-100 focus:border-amber-600 focus:outline-none"
            />
          </div>
          {error && <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300" data-testid="fahrer-cpw-error">{error}</div>}
          <button
            type="submit" disabled={loading} data-testid="fahrer-cpw-submit"
            className="w-full py-3.5 bg-amber-600 hover:bg-amber-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white font-semibold rounded-lg"
          >
            {loading ? t("app.loading") : t("auth.change_password")}
          </button>
        </form>
      </div>
    </div>
  );
}
